import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Header,
  Pagination,
  Tab,
  Table,
  Dropdown,
  Input,
  Icon,
  Popup,
} from "semantic-ui-react";
import tableCss from "../../assets/styles/shared/tables.module.scss";
import css from "../../assets/styles/templateList/index.module.scss";
import imagesCss from "../../assets/styles/images/images.module.scss";
import {
  DeleteIconComponent,
  EditIconComponent,
  LeftArrowIconComponent,
  RightArrowIconComponent,
} from "../iconComponents";
import moment from "moment-timezone";
import { useDispatch } from "react-redux";
import { IMAGE_COPY_CODE_SUCCESS } from "../../constants/imageConstants";

export default function ImagesList(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activePage, setActivePage] = useState(1);
  const [archivedPage, setArchivedPage] = useState(1);
  const [filterTextActive, setFilterTextActive] = useState("");
  const [filterTextArchived, setFilterTextArchived] = useState("");

  const itemsPerPage = 10;

  useEffect(() => {
    props?.fetch();
  }, []);

  const handlePageChange = (tab, page) => {
    if (tab === "active") setActivePage(page);
    else setArchivedPage(page);
  };

  const handleArchive = (archive, id) => {
    props?.archive({ id, archive });
  };

  const handleFilterChange = (tab) => (e) => {
    const value = e.target.value;
    if (tab === "active") {
      setFilterTextActive(value);
      setActivePage(1);
    } else {
      setFilterTextArchived(value);
      setArchivedPage(1);
    }
  };

  const getFilteredImages = (status) => {
    const allImages = props.images?.data || [];
    const filterText =
      status === "active" ? filterTextActive : filterTextArchived;
    return allImages.filter((img) => {
      const isArchived = img.archived;
      const matchesStatus = status === "active" ? !isArchived : isArchived;
      const title = img.title?.toLowerCase() || "";
      return matchesStatus && title.includes(filterText?.toLowerCase());
    });
  };

  const renderTableContents = (status) => {
    const filteredImages = getFilteredImages(status);
    const currentPage = status === "active" ? activePage : archivedPage;
    const totalPages = Math.ceil(filteredImages.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedImages = filteredImages.slice(
      startIndex,
      startIndex + itemsPerPage
    );

    const generateHtmlCode = (slug) => {
      return `<img src="${process.env.REACT_APP_DOMAIN_URL}/image_generation/${slug}?keyword={{KEYWORD_VALUE}}" alt="Image" style="width: 100%; height: 100%;" />`
    }

    const copyCode = (slug) => {
      dispatch({ type: IMAGE_COPY_CODE_SUCCESS });
      navigator.clipboard.writeText(generateHtmlCode(slug));
    }

    return (
      <>
        <div className={imagesCss.images_content}>
          <div>
            <Dropdown
              placeholder="Sort"
              selection
              options={[
                { key: "name", value: "name", text: "Name" },
                { key: "date", value: "date", text: "Creation Date" },
              ]}
              disabled
            />
          </div>
          <div>
            <Input
              icon
              iconPosition="left"
              placeholder="Search"
              value={
                status === "active" ? filterTextActive : filterTextArchived
              }
              onChange={handleFilterChange(status)}
            >
              <input />
              <Icon name="search" />
            </Input>
          </div>
        </div>

        {paginatedImages.length > 0 ? (
          <>
            <Table basic className={tableCss.tableBorder}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={4}>Name</Table.HeaderCell>
                  <Table.HeaderCell width={4}>Creation Date</Table.HeaderCell>
                  <Table.HeaderCell width={4}>Views</Table.HeaderCell>
                  <Table.HeaderCell width={4}>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row className={tableCss.tableRowBackground}>
                  <Table.HeaderCell className={tableCss.tableCol} colSpan="6">
                    {filteredImages.length} total images
                  </Table.HeaderCell>
                </Table.Row>
                {paginatedImages.map((img) => (
                  <Table.Row key={img.id}>
                    <Table.Cell>{img.title}</Table.Cell>
                    <Table.Cell>
                      {moment(img.created_at).format("MMM DD, YYYY HH:mm")}
                    </Table.Cell>
                    <Table.Cell>{img?.views}</Table.Cell>
                    <Table.Cell className={tableCss.actionDiv}>
                      <Popup
                        inverted
                        trigger={
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              navigate(`/images/${img?.id}`);
                            }}
                          >
                            <EditIconComponent />
                          </span>
                        }
                        content="Edit"
                        position="top center"
                      />
                      <Popup
                        inverted
                        trigger={
                          <span onClick={() => copyCode(img?.slug)} style={{ cursor: "pointer" }}>
                            <Icon name="linkify" style={{ color: "#4E84A1" }} />
                          </span>
                        }
                        content="Copy Code"
                        position="top center"
                      />
                      <Popup
                        inverted
                        trigger={
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              handleArchive(!img.archived, img.id);
                            }}
                          >
                            <DeleteIconComponent />
                          </span>
                        }
                        content={img.archived ? "Unarchive" : "Archive"}
                        position="top center"
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            <div style={{ display: "flex", justifyContent: "end" }}>
              <Pagination
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) =>
                  handlePageChange(status, activePage)
                }
                lastItem={null}
                firstItem={null}
                ellipsisItem={{
                  content: <Icon name="ellipsis horizontal" />,
                  icon: true,
                }}
                prevItem={{
                  content: <LeftArrowIconComponent />,
                  icon: true,
                }}
                nextItem={{
                  content: <RightArrowIconComponent />,
                  icon: true,
                }}
              />
            </div>
          </>
        ) : (
          <div className={tableCss.zero}>You do not currently have any {status} images.</div>
        )}
      </>
    );
  };

  const renderTable = (status) => () =>
    (
      <Tab.Pane as="div" attached="false">
        {renderTableContents(status)}
      </Tab.Pane>
    );

  const renderTabs = () => {
    const tables = [
      { menuItem: "Active images", render: renderTable("active") },
      { menuItem: "Archived images", render: renderTable("archived") },
    ];

    return <Tab menu={{ secondary: true, pointing: true }} panes={tables} />;
  };

  return (
    <div
      id="images-list"
      className={`${tableCss.tableContainer} ${imagesCss.imagesContainer}`}
    >
      <Header as="h1">Image Personalization</Header>
      <div className={tableCss.tableActions}>
        <Link to="/images/new">
          <Button className={css.primaryButton}>Create New</Button>
        </Link>
      </div>
      {renderTabs()}
    </div>
  );
}
